import React from 'react';
import PropTypes from 'prop-types';
import map from 'lodash/map';
import first from 'lodash/first';
import styled from 'styled-components';

import isEmpty from 'lodash/isEmpty';
import { DateTimeService } from '@shared/services/DateTimeService';
import { DEFAULT_TIME_ZONE_NAME } from '@shared/constants';
import arrow_left from '../../images/arrow-left.svg';
import arrow_right from '../../images/arrow-right.svg';
import EmptyText from '../empty/EmptyText';
import media from '../../utils/mediaQueries';
import { LINKS_COLOR, TEXT_COLOR } from '../../theme';

const CalendarContainer = styled.div`
  position: relative;
  display: flex;
  //To overcome columns margin: 0 16px and made 12px margin
  margin: 0 -4px 5px;
  ${media.mobile`
    margin: 0 -9px;
    flex-direction: column;
  `}
`;

const NavButton = styled.div`
  position: absolute;
  top: 6px;
  ${(props) => (props.direction === 'left' ? 'left: -24px;' : 'right: -24px;')}
  ${media.mobile`
    ${(props) => (props.direction === 'left' ? 'left: 0;' : 'right: 0;')}
    top: 0;
  `}
  width: 24px;
  height: 24px;
  border-radius: 50%;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.1);
  background-color: #597cbd;
  display: none;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    opacity: 0.8;
  }
  ${(props) => props.isNavVisible
    && `
    display: flex;
  `}
`;

const Image = styled.img`
  width: 16px;
  height: 16px;
`;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Column = styled.div`
  margin: 0 ${(props) => props.columnGap}px;
  ${media.mobile`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(64px, 1fr));
    gap: 3px 6px;
    margin: 8px 0 0;
  `}
`;

const Slot = styled.div`
  margin-bottom: 10px;
  padding: 10px 0;
  width: 64px;
  border-radius: 6px;
  border: solid 1px #a6c8f0;
  font-size: 14px;
  text-align: center;
  cursor: pointer;
  letter-spacing: -0.2px;
  color: ${(props) => (props.active ? '#ffffff' : LINKS_COLOR)};
  ${(props) => props.active
    ? `
    font-weight: bold;
    background-color: ${LINKS_COLOR};
    border-color: ${LINKS_COLOR};
  `
    : `
    &:hover {
      border-color: ${LINKS_COLOR};
      font-weight: bold;
    }
  `}
  transition: all .2s;
  ${media.mobile`
    width: auto;
  `}
`;

const SlotHeader = styled.div`
  font-size: 14px;
  line-height: 1.29;
  letter-spacing: 0.1px;
  text-align: center;
  color: ${TEXT_COLOR};
  margin-bottom: 8px;
  ${media.mobile`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    font-size: 16px;
    line-height: 1.5;
    letter-spacing: 0.2px;
    > div:first-child {
      margin-right: 4px;
    }
  `}
`;

const Slots = (props) => {
  const {
    onClickNavLeft,
    isLeftNavVisible,
    onClickNavRight,
    isRightNavVisible,
    slots,
    onSlotClick,
    timeZoneName,
    emptyText,
    columnGap,
    selectedSlot,
  } = props;

  const renderSlotHeader = (slot) => {
    const slotStartDate = new Date(slot.start_date);
    const { setDateLocale } = DateTimeService;

    return (
      <SlotHeader>
        <div>{setDateLocale(slotStartDate, 'EEEE')}</div>
        <div>{setDateLocale(slotStartDate, 'dd MMM')}</div>
      </SlotHeader>
    );
  };

  return (
    <CalendarContainer>
      <NavButton direction="left" onClick={onClickNavLeft} isNavVisible={isLeftNavVisible}>
        <Image src={arrow_left} alt="flèche droite" />
      </NavButton>
      <NavButton direction="right" onClick={onClickNavRight} isNavVisible={isRightNavVisible}>
        <Image src={arrow_right} alt="flèche droite" />
      </NavButton>
      {isEmpty(slots) ? (
        <EmptyText text={emptyText} />
      ) : (
        map(slots, (column, index) => (
          <ColumnContainer key={`booking-column${index}`}>
            {first(column) && renderSlotHeader(first(column))}
            <Column columnGap={columnGap}>
              {map(column, (slot, yindex) => (
                <Slot
                  key={`booking-slot${yindex}`}
                  onClick={() => onSlotClick(slot)}
                  active={
                    selectedSlot?.start_date
                    && new Date(selectedSlot.start_date).toISOString()
                      === new Date(slot.start_date).toISOString()
                  }
                >
                  {DateTimeService.formatInTimeZone(
                    new Date(slot.start_date),
                    timeZoneName,
                    'HH:mm',
                  )}
                </Slot>
              ))}
            </Column>
          </ColumnContainer>
        ))
      )}
    </CalendarContainer>
  );
};

Slots.propTypes = {
  slots: PropTypes.object,
  onSlotClick: PropTypes.func,
  onClickNavLeft: PropTypes.func,
  isLeftNavVisible: PropTypes.bool,
  onClickNavRight: PropTypes.func,
  isRightNavVisible: PropTypes.bool,
  timeZoneName: PropTypes.string,
  emptyText: PropTypes.string,
  columnGap: PropTypes.number,
  selectedSlot: PropTypes.object,
};

Slots.defaultProps = {
  slots: {},
  onSlotClick: () => {},
  onClickNavLeft: () => {},
  isLeftNavVisible: true,
  onClickNavRight: () => {},
  isRightNavVisible: true,
  timeZoneName: DEFAULT_TIME_ZONE_NAME,
  emptyText: '',
  columnGap: 16,
  selectedSlot: {},
};

export { Slots };
